import {
  StudioCapabilityContractError,
} from "./errors";

export const STUDIO_CAPABILITY_TYPES = Object.freeze([
  "toolbar",
  "sidebar",
  "inspector",
  "canvas",
  "interaction",
  "serializer",
  "persistence",
  "import",
  "export",
]);

function isNonEmptyString(value) {
  return typeof value === "string" && value.trim() !== "";
}

function isPlainObject(value) {
  if (value === null || typeof value !== "object") {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);

  return prototype === Object.prototype || prototype === null;
}

function normalizePriority(priority) {
  if (!Number.isFinite(priority)) {
    throw new StudioCapabilityContractError(
      "StudioCapability.priority must be a finite number.",
      "INVALID_CAPABILITY_PRIORITY"
    );
  }

  return priority;
}

export function createStudioCapability({
  id,
  type,
  contribution,
  priority = 0,
  metadata = {},
} = {}) {
  if (!isNonEmptyString(id)) {
    throw new StudioCapabilityContractError(
      "StudioCapability.id must be a non-empty string.",
      "INVALID_CAPABILITY_ID"
    );
  }

  if (!isNonEmptyString(type)) {
    throw new StudioCapabilityContractError(
      "StudioCapability.type must be a non-empty string.",
      "INVALID_CAPABILITY_TYPE"
    );
  }

  if (typeof contribution === "undefined") {
    throw new StudioCapabilityContractError(
      "StudioCapability.contribution is required.",
      "MISSING_CAPABILITY_CONTRIBUTION"
    );
  }

  if (!isPlainObject(metadata)) {
    throw new StudioCapabilityContractError(
      "StudioCapability.metadata must be a plain object.",
      "INVALID_CAPABILITY_METADATA"
    );
  }

  return Object.freeze({
    id: id.trim(),
    type: type.trim(),
    contribution,
    priority: normalizePriority(priority),
    metadata: Object.freeze({ ...metadata }),
  });
}

export function isStudioCapability(value) {
  return Boolean(
    value
      && typeof value === "object"
      && isNonEmptyString(value.id)
      && isNonEmptyString(value.type)
      && Object.prototype.hasOwnProperty.call(value, "contribution")
      && Number.isFinite(value.priority)
      && isPlainObject(value.metadata)
  );
}

export function assertStudioCapability(value) {
  if (!isStudioCapability(value)) {
    throw new StudioCapabilityContractError(
      "Expected a valid StudioCapability.",
      "INVALID_STUDIO_CAPABILITY"
    );
  }

  return value;
}
